// 语音录制和实时转文字服务
// 移除钉钉认证依赖

export interface VoiceRecordingConfig {
    websocketUrl?: string;
    sampleRate?: number;
    bufferSize?: number;
    maxDurationMs?: number;
}

export interface TranscriptionResult {
    text: string;
    isFinal: boolean;
    confidence?: number;
    timestamp?: number;
}

export class VoiceRecordingService {
    private mediaRecorder: MediaRecorder | null = null;
    private audioContext: AudioContext | null = null;
    private websocket: WebSocket | null = null;
    private isRecording = false;
    private audioStream: MediaStream | null = null;
    private processor: ScriptProcessorNode | null = null;
    private maxDurationTimer: number | null = null;
    private recordedChunks: BlobPart[] = [];
    private recordingStartTimeMs: number | null = null;
    private pendingRecordingResult: { blob: Blob; durationMs: number; mimeType: string } | null = null;
    // 累积原始PCM以生成WAV
    private pcmDataChunks: Float32Array[] = [];

    private config: VoiceRecordingConfig = {
        websocketUrl: process.env.REACT_APP_VOICE_WS_URL || 'wss://ai.kumhosunny.cn:7088/webSocket',
        // websocketUrl: process.env.REACT_APP_VOICE_WS_URL || 'ws://localhost:8999/webSocket',
        sampleRate: 16000,
        bufferSize: 4096,
        maxDurationMs: 60000
    };

    private onTranscriptionCallback?: (result: TranscriptionResult) => void;
    private onErrorCallback?: (error: Error) => void;
    private onStatusChangeCallback?: (status: 'connecting' | 'connected' | 'recording' | 'stopped' | 'error') => void;
    private onRecordingCompleteCallback?: (payload: { blob: Blob; durationMs: number; mimeType: string }) => void;
    
    // 去重相关
    private lastTranscriptionText: string = '';
    private lastFinalText: string = '';
    private processedFinalTexts: Set<string> = new Set(); // 记录已处理的最终结果

    constructor(config?: Partial<VoiceRecordingConfig>) {
        this.config = { ...this.config, ...config };
    }

    // 设置回调函数
    setCallbacks(callbacks: {
        onTranscription?: (result: TranscriptionResult) => void;
        onError?: (error: Error) => void;
        onStatusChange?: (status: 'connecting' | 'connected' | 'recording' | 'stopped' | 'error') => void;
        onRecordingComplete?: (payload: { blob: Blob; durationMs: number; mimeType: string }) => void;
    }) {
        this.onTranscriptionCallback = callbacks.onTranscription;
        this.onErrorCallback = callbacks.onError;
        this.onStatusChangeCallback = callbacks.onStatusChange;
        this.onRecordingCompleteCallback = callbacks.onRecordingComplete;
    }

    // 初始化WebSocket连接（单地址）
    private async initWebSocket(): Promise<void> {
        return new Promise((resolve, reject) => {
            try {
                this.onStatusChangeCallback?.('connecting');

                // 计算唯一地址：优先环境变量；否则本地页面用 ws://localhost:8999/webSocket；否则远程默认
                const envUrl = process.env.REACT_APP_VOICE_WS_URL;
                const isLocalPage = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/i.test(window.location?.hostname || '');
                const url = envUrl || (isLocalPage ? 'ws://localhost:8999/webSocket' : 'wss://ai.kumhosunny.cn:7088/webSocket');
                this.config.websocketUrl = url;
                console.log('语音WS连接地址:', url);

                this.websocket = new WebSocket(url);

                this.websocket.onopen = () => {
                    console.log('WebSocket连接已建立');
                    this.onStatusChangeCallback?.('connected');
                    resolve();
                };

                this.websocket.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        // 处理不同格式的转录结果
                        if (data.text !== undefined && typeof data.text === 'string' && data.text.trim()) {
                            const result: TranscriptionResult = {
                                text: data.text.trim(),
                                isFinal: data.isFinal !== false,
                                confidence: data.confidence,
                                timestamp: Date.now()
                            };

                            if (result.isFinal) {
                                // 使用更强的去重机制
                                const normalizedText = result.text.trim().toLowerCase();
                                if (!this.processedFinalTexts.has(normalizedText) && result.text !== this.lastFinalText) {
                                    this.lastFinalText = result.text;
                                    this.processedFinalTexts.add(normalizedText);
                                    this.lastTranscriptionText = '';
                                    console.log('发送最终转录结果:', result.text);
                                    this.onTranscriptionCallback?.(result);
                                } else {
                                    console.log('跳过重复的最终转录结果:', result.text);
                                }
                            } else {
                                if (result.text !== this.lastTranscriptionText) {
                                    this.lastTranscriptionText = result.text;
                                    this.onTranscriptionCallback?.(result);
                                }
                            }
                        }
                    } catch (error) {
                        console.error('解析转录结果失败:', error);
                    }
                };

                this.websocket.onerror = (error) => {
                    console.error('WebSocket错误:', error);
                    this.onStatusChangeCallback?.('error');
                    const errorMessage = 'WebSocket连接错误，请检查网络连接或服务器状态';
                    console.error('WebSocket详细错误:', errorMessage);
                    this.onErrorCallback?.(new Error(errorMessage));
                    reject(new Error(errorMessage));
                };

                this.websocket.onclose = (event) => {
                    console.log('WebSocket连接已关闭:', event.code, event.reason);
                    this.onStatusChangeCallback?.('stopped');
                    if (event.code !== 1000) {
                        this.onErrorCallback?.(new Error(`WebSocket连接异常关闭: ${event.reason}`));
                    }
                };

                // 设置连接超时（延长至15秒，给权限处理更多时间）
                setTimeout(() => {
                    if (this.websocket?.readyState !== WebSocket.OPEN) {
                        this.websocket?.close();
                        reject(new Error('WebSocket连接超时'));
                    }
                }, 15000);

            } catch (error) {
                reject(error);
            }
        });
    }

    // 多地址逻辑已移除；保留空方法占位避免引用报错（若外部仍调用不受影响）
    private buildCandidateWsUrls(): string[] {
        return [this.config.websocketUrl || ''];
    }

    // 带重试机制的WebSocket连接
    private async initWebSocketWithRetry(maxRetries: number = 3): Promise<void> {
        let lastError: Error | null = null;
        
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                console.log(`WebSocket连接尝试 ${attempt}/${maxRetries}`);
                
                // 清理之前的连接
                if (this.websocket) {
                    this.websocket.close();
                    this.websocket = null;
                }
                
                await this.initWebSocket();
                console.log(`WebSocket连接成功（第${attempt}次尝试）`);
                return; // 连接成功，退出重试循环
                
            } catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                console.warn(`WebSocket连接失败（第${attempt}次尝试）:`, lastError.message);
                
                if (attempt < maxRetries) {
                    // 指数退避：第一次等待1秒，第二次等待2秒
                    const delay = attempt * 1000;
                    console.log(`${delay}ms后重试...`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                } else {
                    console.error('WebSocket连接重试次数已用尽');
                }
            }
        }
        
        // 所有重试都失败了
        throw new Error(`WebSocket连接失败，已重试${maxRetries}次。最后错误: ${lastError?.message}`);
    }

    // 已废弃：移除重复的WebSocket连接逻辑，防止消息处理重复
    private async tryConnectWebSocket(url: string): Promise<void> {
        // 此方法已废弃，所有连接逻辑都通过 initWebSocketWithRetry 处理
        throw new Error('tryConnectWebSocket方法已废弃，请使用initWebSocketWithRetry');
    }

    // 检查麦克风权限状态
    private async checkMicrophonePermission(): Promise<boolean> {
        try {
            // 检查浏览器是否支持权限API
            if (!navigator.permissions) {
                console.warn('浏览器不支持权限API，尝试直接获取麦克风权限');
                return true; // 继续尝试获取权限
            }

            const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
            console.log('麦克风权限状态:', permission.state);
            
            switch (permission.state) {
                case 'granted':
                    return true;
                case 'denied':
                    throw new Error('麦克风权限被拒绝，请在浏览器设置中允许麦克风访问');
                case 'prompt':
                    return true; // 需要用户确认，继续申请权限
                default:
                    return true;
            }
        } catch (error) {
            console.warn('检查麦克风权限失败:', error);
            return true; // 如果检查失败，继续尝试获取权限
        }
    }

    // 获取麦克风权限
    private async getMicrophonePermission(): Promise<MediaStream> {
        try {
            // 检查浏览器支持
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                throw new Error('您的浏览器不支持麦克风功能，请使用Chrome、Firefox或Safari等现代浏览器');
            }

            console.log('正在申请麦克风权限...');
            
            // 直接申请麦克风权限（移除预检查，避免权限状态查询的兼容性问题）
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: this.config.sampleRate,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                },
                video: false
            });
            
            console.log('麦克风权限获取成功');
            
            // 检查音频轨道
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length === 0) {
                stream.getTracks().forEach(track => track.stop()); // 清理流
                throw new Error('未找到可用的音频设备');
            }
            
            console.log('音频设备信息:', audioTracks[0].label || '默认麦克风');
            return stream;
            
        } catch (error) {
            console.error('获取麦克风权限失败:', error);
            
            // 根据错误类型提供具体的错误信息
            if (error instanceof Error) {
                if (error.name === 'NotAllowedError') {
                    throw new Error('麦克风权限被拒绝。请点击地址栏的🔒图标，允许麦克风访问，然后刷新页面重试');
                } else if (error.name === 'NotFoundError') {
                    throw new Error('未找到麦克风设备，请检查您的设备是否连接了麦克风');
                } else if (error.name === 'NotReadableError') {
                    throw new Error('麦克风被其他应用占用，请关闭其他使用麦克风的应用后重试');
                } else if (error.name === 'OverconstrainedError') {
                    throw new Error('麦克风不支持所需的音频格式，请尝试使用其他设备');
                } else if (error.name === 'SecurityError') {
                    throw new Error('安全限制：请确保网站使用HTTPS协议访问');
                } else {
                    throw new Error(`麦克风访问失败: ${error.message}`);
                }
            } else {
                throw new Error('无法访问麦克风，请检查浏览器权限设置');
            }
        }
    }

    // 初始化音频处理
    private async initAudioProcessing(stream: MediaStream): Promise<void> {
        try {
            // 创建音频上下文
            this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
                sampleRate: this.config.sampleRate
            });

            // 创建音频源
            const source = this.audioContext.createMediaStreamSource(stream);

            // 创建音频处理器
            this.processor = this.audioContext.createScriptProcessor(
                this.config.bufferSize,
                1, // 输入通道
                1  // 输出通道
            );

            // 处理音频数据
            this.processor.onaudioprocess = (event) => {
                if (!this.isRecording || !this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
                    return;
                }

                const inputBuffer = event.inputBuffer;
                const audioData = inputBuffer.getChannelData(0);
                // 复制一份原始数据，后续用于生成WAV
                const copied = new Float32Array(audioData.length);
                copied.set(audioData);
                this.pcmDataChunks.push(copied);
                
                // 转换为16位PCM格式
                const pcmData = this.float32ToPCM16(audioData);
                
                // 发送音频数据到WebSocket
                if (this.websocket.readyState === WebSocket.OPEN) {
                    this.websocket.send(pcmData.buffer);
                }
            };

            // 连接音频节点
            source.connect(this.processor);
            this.processor.connect(this.audioContext.destination);

            console.log('音频处理初始化完成');
        } catch (error) {
            console.error('音频处理初始化失败:', error);
            throw error;
        }
    }

    // 初始化原始音频录制（用于生成可回放的音频消息）
    private initMediaRecorder(stream: MediaStream): void {
        try {
            const mimeType = this.chooseSupportedMimeType([
                'audio/webm;codecs=opus',
                'audio/webm',
                'audio/mp4',
                'audio/ogg;codecs=opus'
            ]);

            this.recordedChunks = [];
            this.pcmDataChunks = [];
            this.mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            this.recordingStartTimeMs = Date.now();

            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data && event.data.size > 0) {
                    this.recordedChunks.push(event.data);
                }
            };

            this.mediaRecorder.onstop = () => {
                try {
                    // 暂不在此最终确定结果，等待stopRecording中用PCM生成WAV后覆盖
                } catch (e) {
                    console.error('生成录音文件失败:', e);
                    this.pendingRecordingResult = null;
                } finally {
                    this.recordedChunks = [];
                    this.recordingStartTimeMs = null;
                }
            };
        } catch (e) {
            console.warn('初始化 MediaRecorder 失败，将无法生成音频消息:', e);
            this.mediaRecorder = null;
        }
    }

    private chooseSupportedMimeType(candidates: string[]): string | null {
        if (!(window as any).MediaRecorder) return null;
        for (const t of candidates) {
            if ((MediaRecorder as any).isTypeSupported && (MediaRecorder as any).isTypeSupported(t)) {
                return t;
            }
        }
        return null;
    }

    // 将Float32Array转换为16位PCM
    private float32ToPCM16(float32Array: Float32Array): Int16Array {
        const pcm16 = new Int16Array(float32Array.length);
        for (let i = 0; i < float32Array.length; i++) {
            // 将浮点数转换为16位整数
            const sample = Math.max(-1, Math.min(1, float32Array[i]));
            pcm16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7FFF;
        }
        return pcm16;
    }

    // 清理录音时长计时器
    private clearDurationTimer(): void {
        if (this.maxDurationTimer !== null) {
            window.clearTimeout(this.maxDurationTimer);
            this.maxDurationTimer = null;
        }
    }

    // 开始录音
    async startRecording(): Promise<void> {
        try {
            if (this.isRecording) {
                console.warn('录音已在进行中，跳过重复启动');
                return;
            }

            // 检查WebSocket连接状态，避免重复连接
            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                console.warn('WebSocket已连接，跳过重复初始化');
                this.isRecording = true;
                this.onStatusChangeCallback?.('recording');
                return;
            }

            console.log('开始录音...');

            // 重置去重状态
            this.lastTranscriptionText = '';
            this.lastFinalText = '';
            this.processedFinalTexts.clear(); // 清空已处理的最终结果记录

            // 优化后的初始化顺序：
            // 1. 先获取麦克风权限（避免权限弹窗导致WebSocket超时）
            console.log('步骤1: 获取麦克风权限...');
            this.audioStream = await this.getMicrophonePermission();

            // 2. 初始化WebSocket连接（在有权限后连接，避免超时）
            console.log('步骤2: 初始化WebSocket连接...');
            await this.initWebSocketWithRetry();

            // 3. 初始化音频处理
            console.log('步骤3: 初始化音频处理...');
            await this.initAudioProcessing(this.audioStream);

            // 4. 初始化原始录音
            console.log('步骤4: 初始化录音器...');
            this.initMediaRecorder(this.audioStream);
            if (this.mediaRecorder && this.mediaRecorder.state !== 'recording') {
                try {
                    this.mediaRecorder.start(500); // 每 500ms 触发一次 dataavailable
                } catch (e) {
                    console.warn('启动 MediaRecorder 失败:', e);
                }
            }

            // 5. 开始录音
            this.isRecording = true;
            this.onStatusChangeCallback?.('recording');

            console.log('录音开始成功');

            // 启动最大录音时长计时器
            this.clearDurationTimer();
            this.maxDurationTimer = window.setTimeout(async () => {
                try {
                    console.log('录音已达到最大时长，自动停止');
                    await this.stopRecording();
                } catch (e) {
                    console.error('自动停止录音失败:', e);
                }
            }, this.config.maxDurationMs);

        } catch (error) {
            console.error('开始录音失败:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('详细错误信息:', errorMessage);
            this.onErrorCallback?.(new Error(errorMessage));
            await this.stopRecording();
            throw new Error(errorMessage);
        }
    }

    // 停止录音
    async stopRecording(): Promise<void> {
        try {
            console.log('停止录音...');
            this.clearDurationTimer();
            
            this.isRecording = false;

            // 停止原始音频录制
            if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
                try {
                    this.mediaRecorder.stop();
                } catch (e) {
                    console.warn('停止 MediaRecorder 失败:', e);
                }
            }

            // 发送结束信号并等待最后的转录结果
            if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                // 发送结束信号
                this.websocket.send(JSON.stringify({ type: 'end' }));
                
                // 等待一小段时间以接收最后的转录结果
                await new Promise(resolve => setTimeout(resolve, 500));
            }

            // 基于已累积的PCM生成WAV文件
            try {
                if (this.pcmDataChunks.length > 0) {
                    const wavBlob = this.buildWavFromPcm(this.pcmDataChunks, this.config.sampleRate || 16000);
                    const totalSamples = this.pcmDataChunks.reduce((sum, arr) => sum + arr.length, 0);
                    const durationMs = Math.round((totalSamples / (this.config.sampleRate || 16000)) * 1000);
                    this.pendingRecordingResult = {
                        blob: wavBlob,
                        durationMs,
                        mimeType: 'audio/wav'
                    };
                }
            } catch (e) {
                console.warn('生成WAV失败，回退到MediaRecorder结果（如有）:', e);
            } finally {
                this.pcmDataChunks = [];
            }

            // 在确保已接收最后的转录结果后，再回调录音完成，保证文字完整
            if (this.pendingRecordingResult) {
                const payload = this.pendingRecordingResult;
                this.pendingRecordingResult = null;
                try {
                    this.onRecordingCompleteCallback?.(payload);
                } catch (e) {
                    console.warn('回调录音完成处理失败:', e);
                }
            }

            // 停止音频流
            if (this.audioStream) {
                this.audioStream.getTracks().forEach(track => track.stop());
                this.audioStream = null;
            }

            // 清理音频处理
            if (this.processor) {
                this.processor.disconnect();
                this.processor = null;
            }

            if (this.audioContext) {
                await this.audioContext.close();
                this.audioContext = null;
            }

            // 关闭WebSocket
            if (this.websocket) {
                this.websocket.close(1000, 'Recording stopped');
                this.websocket = null;
            }

            this.onStatusChangeCallback?.('stopped');
            console.log('录音停止成功');

        } catch (error) {
            console.error('停止录音失败:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('停止录音详细错误信息:', errorMessage);
            this.onErrorCallback?.(new Error(errorMessage));
        }
    }

    // 获取录音状态
    getRecordingStatus(): boolean {
        return this.isRecording;
    }

    // 获取WebSocket连接状态
    getConnectionStatus(): string {
        if (!this.websocket) return 'disconnected';
        
        switch (this.websocket.readyState) {
            case WebSocket.CONNECTING:
                return 'connecting';
            case WebSocket.OPEN:
                return 'connected';
            case WebSocket.CLOSING:
                return 'closing';
            case WebSocket.CLOSED:
                return 'closed';
            default:
                return 'unknown';
        }
    }

    // 预先检查麦克风权限（供外部调用）
    async checkMicrophoneAccess(): Promise<{ hasAccess: boolean; message: string }> {
        try {
            // 简化权限检查，只使用浏览器权限

            // 检查浏览器支持
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                return {
                    hasAccess: false,
                    message: '您的浏览器不支持麦克风功能，请使用Chrome、Firefox或Safari等现代浏览器'
                };
            }

            // 检查权限状态
            if (navigator.permissions) {
                try {
                    const permission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
                    if (permission.state === 'denied') {
                        return {
                            hasAccess: false,
                            message: '麦克风权限被拒绝，请在浏览器设置中允许麦克风访问'
                        };
                    }
                } catch (error) {
                    console.warn('无法查询麦克风权限状态:', error);
                }
            }

            // 尝试获取设备列表
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioInputDevices = devices.filter(device => device.kind === 'audioinput');
                
                if (audioInputDevices.length === 0) {
                    return {
                        hasAccess: false,
                        message: '未检测到麦克风设备，请连接麦克风后重试'
                    };
                }

                console.log('检测到音频输入设备:', audioInputDevices.length, '个');
            } catch (error) {
                console.warn('无法枚举音频设备:', error);
            }

            return {
                hasAccess: true,
                message: '麦克风权限检查通过'
            };

        } catch (error) {
            console.error('检查麦克风权限失败:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error('麦克风权限检查详细错误:', errorMessage);
            return {
                hasAccess: false,
                message: `无法检查麦克风权限: ${errorMessage}`
            };
        }
    }

    // 清理资源
    async cleanup(): Promise<void> {
        await this.stopRecording();
    }

    // 构建WAV Blob（PCM 16-bit mono）
    private buildWavFromPcm(chunks: Float32Array[], sampleRate: number): Blob {
        const numChannels = 1;
        const bytesPerSample = 2; // 16-bit
        const totalSamples = chunks.reduce((sum, c) => sum + c.length, 0);

        // 合并为一个Float32Array
        const merged = new Float32Array(totalSamples);
        let offset = 0;
        for (const c of chunks) {
            merged.set(c, offset);
            offset += c.length;
        }

        // 转为 Int16 PCM
        const pcm16 = this.float32ToPCM16(merged);

        const blockAlign = numChannels * bytesPerSample;
        const byteRate = sampleRate * blockAlign;
        const dataSize = pcm16.byteLength;
        const wavHeaderSize = 44;
        const totalSize = wavHeaderSize + dataSize;

        const buffer = new ArrayBuffer(totalSize);
        const view = new DataView(buffer);

        // RIFF header
        this.writeString(view, 0, 'RIFF');
        view.setUint32(4, 36 + dataSize, true);
        this.writeString(view, 8, 'WAVE');

        // fmt subchunk
        this.writeString(view, 12, 'fmt ');
        view.setUint32(16, 16, true); // Subchunk1Size for PCM
        view.setUint16(20, 1, true); // AudioFormat PCM
        view.setUint16(22, numChannels, true);
        view.setUint32(24, sampleRate, true);
        view.setUint32(28, byteRate, true);
        view.setUint16(32, blockAlign, true);
        view.setUint16(34, 8 * bytesPerSample, true); // BitsPerSample

        // data subchunk
        this.writeString(view, 36, 'data');
        view.setUint32(40, dataSize, true);

        // PCM data
        new Uint8Array(buffer, wavHeaderSize).set(new Uint8Array(pcm16.buffer));

        return new Blob([buffer], { type: 'audio/wav' });
    }

    private writeString(view: DataView, offset: number, str: string) {
        for (let i = 0; i < str.length; i++) {
            view.setUint8(offset + i, str.charCodeAt(i));
        }
    }
}

// 导出单例实例
export const voiceRecordingService = new VoiceRecordingService();
export default VoiceRecordingService;
